import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CSS } from '@dnd-kit/utilities';
import { useSortable } from '@dnd-kit/sortable';
import { Icon, Clock, Lock, Trash2, MapPin, Navigation, X, Map as MapIcon, ExternalLink, CircleDashed, CheckCircle2 } from '../../ui/Icon';
import ConfirmModal from '../../ui/ConfirmModal';
import Spinner from '../../ui/Spinner';
import type { Activity, ActivityType } from '../../../types';
import { buildGoogleMapsUrl } from '../../../utils/googleMaps';
import { endTimeOf, type TimeSuggestion } from '../../../utils/activityOrder';
import { formatMinutes } from '../../../utils/duration';

const CAT_DOTS: Record<ActivityType, string> = {
  TRANSPORT:    '#0ea5e9',
  PARKING:      '#0ea5e9',
  BEACH:        '#c87a1a',
  NATURE:       '#c87a1a',
  ACCOMMODATION:'#6366f1',
  MONUMENT:     '#8b5cf6',
  VIEWPOINT:    '#8b5cf6',
  NEIGHBORHOOD: '#8b5cf6',
  RESTAURANT:   '#f97316',
  CAFE:         '#f97316',
  EXPERIENCE:   '#c87a1a',
  PARK:         '#c87a1a',
  SHOP:         '#c87a1a',
  OTHER:        '#94a3b8',
};

interface Props {
  activity: Activity;
  isFirst?: boolean;
  onEdit: (a: Activity) => void;
  onDelete: (a: Activity) => void;
  isSelected?: boolean;
  onToggleSelect?: () => void;
  isLocked?: boolean;
  onToggleStartFromPrev?: (enabled: boolean) => void;
  startFromPrevPending?: boolean;
  /** The hour the app worked out for this stop, when it has none of its own. */
  suggestion?: TimeSuggestion | null;
  /** Takes the suggestion. Absent = it is not offered. */
  onAcceptSuggestion?: () => void;
  // Resolves the Google Maps link for this activity. The itinerary passes one
  // that reuses the linked venue's canonical URL, so a saved place opens the
  // exact same way it does in the Places tab.
  getMapsUrl?: (a: Activity) => string | null;
  /**
   * "Visited" belongs to the saved place, not to the stop (`Venue.visited`), so
   * this pair only arrives for a stop that has one. **No handler, no button**:
   * a stop with nowhere to write it has nothing to mark, and a dead toggle lies
   * worse than an absent one.
   */
  visited?: boolean;
  onToggleVisited?: () => void;
  /** Origin card only: the day's start hour, which is when you leave from here (ADR-0010). */
  departureTime?: string | null;
}

export function ActivityCard({ activity, isFirst = false, onEdit, onDelete, isSelected, onToggleSelect, isLocked, onToggleStartFromPrev, startFromPrevPending, suggestion, onAcceptSuggestion, getMapsUrl, visited, onToggleVisited, departureTime }: Props) {
  const { t } = useTranslation();
  const shownTime = activity.isOrigin ? departureTime : activity.startTime;
  // isOrigin and locked activities are not draggable
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: activity.id, disabled: activity.isOrigin });
  const [confirmDelete, setConfirmDelete] = useState(false);

  const dotColor = CAT_DOTS[activity.type] ?? '#94a3b8';
  const mapsUrl = activity.isOrigin
    ? null
    : getMapsUrl
      ? getMapsUrl(activity)
      : buildGoogleMapsUrl({
          name: activity.venueName || activity.title,
          placeId: activity.placeId,
          latitude: activity.latitude,
          longitude: activity.longitude,
          address: activity.address,
          zone: activity.zone,
        });
  const selectable = !!onToggleSelect && !activity.isOrigin;
  const draggable = !activity.isOrigin;
  const deletable = !activity.isOrigin;
  // The origin card mirrors the previous day's last stop — it isn't a stop of
  // this day, so there is nothing to skip.
  const optional = activity.isOptional && !activity.isOrigin;

  return (
    <div
      ref={setNodeRef}
      // Whole card is the drag handle — listeners spread here, not on a grip button
      {...(draggable ? { ...attributes, ...listeners } : {})}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}
      className={[
        'flex items-start rounded-xl border transition-all overflow-hidden shadow-sm select-none',
        draggable ? 'cursor-grab active:cursor-grabbing' : '',
        // Dashed outline reads as "not fixed" at a glance, before any label is read
        optional ? 'border-dashed' : '',
        isSelected
          ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-400 dark:border-teal-500 ring-1 ring-teal-300 dark:ring-teal-700'
          : activity.isOrigin
            ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-700'
            : isFirst
              ? 'bg-[#f7fbfd] border-slate-100 dark:border-slate-700 dark:bg-slate-800/40 hover:border-slate-200 dark:hover:border-slate-600'
              : 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-700 hover:border-slate-200 dark:hover:border-slate-600',
      ].filter(Boolean).join(' ')}
    >
      {/* isOrigin pin indicator */}
      {activity.isOrigin && (
        <div className="self-stretch px-2 shrink-0 flex items-center border-r border-teal-200 dark:border-teal-700 text-teal-400">
          <Icon as={MapPin} size={14} />
        </div>
      )}

      {/* Clickable content — click opens edit; dnd distance constraint keeps drag working too */}
      <button
        type="button"
        onClick={() => onEdit(activity)}
        className="flex-1 flex items-start gap-3 px-3.5 py-3 text-left cursor-pointer min-w-0"
      >
        <div className="flex flex-col items-center gap-1.5 shrink-0 pt-1">
          <div className="w-3 h-3 rounded-full" style={{ background: dotColor }} />
          {shownTime && (
            <span className="flex flex-col items-center gap-0.5 leading-none">
              <span className="font-mono text-[11px] font-semibold text-slate-400 dark:text-slate-500 leading-none">
                {shownTime}
              </span>
              {/* ADR-0010: the end time is what makes the duration visible, and
                  the duration is the field people have to learn to fill in for
                  the schedule to be worth anything. A locked hour says so — it
                  is the one the day is built around. */}
              {endTimeOf(activity) && (
                /* Was 9px slate-300 with a leading dash, which read as "minus
                   07:50" at a glance and was barely there at all. The en dash
                   sits on its own faint line so the two numbers still read as
                   one range, and the hour itself gets the same weight as the
                   start — it is a time, not a footnote. */
                <>
                  <span aria-hidden className="font-mono text-[9px] text-slate-300 dark:text-slate-600 leading-none -my-0.5">
                    –
                  </span>
                  <span className="font-mono text-[11px] font-semibold text-slate-400/80 dark:text-slate-500/80 leading-none whitespace-nowrap">
                    {endTimeOf(activity)}
                  </span>
                </>
              )}
              {activity.startTimeLocked && (
                <Icon as={Lock} size={9} className="text-slate-400 dark:text-slate-500" aria-label={t('itinerary.schedule.locked')} />
              )}
            </span>
          )}
          {/* ADR-0010: the app does the arithmetic, the person keeps the result.
              This is offered where an hour is missing and never writes on its
              own — a suggestion that set itself would be a second writer of
              `startTime`, which is the thing that ADR forbids. */}
          {!activity.startTime && suggestion && onAcceptSuggestion && (
            <button
              type="button"
              onClick={e => { e.stopPropagation(); onAcceptSuggestion(); }}
              title={suggestion.travelUnknown
                ? t('itinerary.suggestTime.noTravel')
                : t('itinerary.suggestTime.hint')}
              aria-label={`${t('itinerary.suggestTime.use')} ${suggestion.time}`}
              className="flex items-center leading-none cursor-pointer font-mono text-[11px] font-semibold whitespace-nowrap border-b border-dashed border-slate-300 dark:border-slate-600 text-slate-300 dark:text-slate-600 hover:text-teal-600 dark:hover:text-teal-400 hover:border-teal-500 transition-colors"
            >
              {/* No visible word here on purpose: "use" uppercased is "USA" in
                  Catalan, and a country code beside a time is worse than no
                  label. The dashed underline carries the affordance, the tilde
                  carries the estimate, and the word survives as the aria-label
                  so it is still announced. */}
              {suggestion.estimated ? '~' : ''}{suggestion.time}
            </button>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="text-[15px] font-semibold text-slate-900 dark:text-slate-100 leading-snug min-w-0 break-words">{activity.title}</p>
            {activity.customType && !activity.isOrigin && (
              <span className="text-2xs font-semibold px-1.5 py-0.5 rounded-full bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-300 shrink-0">
                {activity.customType}
              </span>
            )}
            {activity.isOrigin && (
              <span className="text-2xs font-semibold px-1.5 py-0.5 rounded-full bg-teal-100 dark:bg-teal-900/40 text-teal-600 dark:text-teal-400 shrink-0">
                {t('itinerary.startFromPrev.active')}
              </span>
            )}
            {optional && (
              <span
                title={t('itinerary.optional.hint')}
                className="inline-flex items-center gap-1 text-2xs font-semibold px-1.5 py-0.5 rounded-full border border-dashed border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 shrink-0"
              >
                <Icon as={CircleDashed} size={9} />
                {t('itinerary.optional.badge')}
              </span>
            )}
            {isLocked && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-teal-100 dark:bg-teal-900/40 text-teal-600 dark:text-teal-400 shrink-0">
                <Icon as={Navigation} size={9} />
                →
              </span>
            )}
            {(activity.hotelId || activity.flightId || activity.bookingId) && (
              <span
                title={t('itinerary.fromBookingHint')}
                className="text-2xs font-semibold px-1.5 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 shrink-0"
              >
                {t('itinerary.fromBooking')}
              </span>
            )}
            {activity.source === 'IMPORT' && (
              <span
                title={t('common.fromDocumentHint')}
                className="text-2xs font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 shrink-0"
              >
                {t('common.fromDocument')}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 mt-1">
            {activity.isOrigin && (
              <span className="text-xs text-slate-500 dark:text-slate-400">{t('itinerary.startFromPrev.lastOfPrevDay')}</span>
            )}
            {activity.durationMins && (
              <span className="flex items-center gap-1 font-mono text-xs text-slate-400">
                <Icon as={Clock} size={11} />
                {formatMinutes(activity.durationMins)}
              </span>
            )}
            {!activity.isOrigin && activity.venueName && (
              <span className="font-mono text-xs text-slate-500 dark:text-slate-400 truncate">{activity.venueName}</span>
            )}
            {!activity.isOrigin && activity.zone && (
              <span className="font-mono text-xs text-teal-600 dark:text-teal-400">{activity.zone}</span>
            )}
          </div>
          {/* break-words: imported notes routinely carry a bare Google Maps URL,
              one unbroken token wider than the card — without it, it slides under
              the action buttons and gets clipped mid-link. */}
          {activity.notes && (
            <p className="text-xs text-slate-400 dark:text-slate-500 italic mt-1.5 leading-relaxed break-words">{activity.notes}</p>
          )}
        </div>
      </button>

      {/* Mark the stop's saved place as visited without leaving the day — the
          same toggle the map's venue card has, where it was the only one. */}
      {!activity.isOrigin && onToggleVisited && (
        <button
          type="button"
          onPointerDown={e => e.stopPropagation()}
          onClick={e => { e.stopPropagation(); onToggleVisited(); }}
          className={`self-stretch px-2.5 transition-colors shrink-0 border-l border-slate-100 dark:border-slate-700 flex items-center cursor-pointer ${
            visited
              ? 'text-green-500 hover:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50'
              : 'text-slate-300 hover:text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20'
          }`}
          aria-label={visited ? t('venues.markUnvisited') : t('venues.markVisited')}
          title={visited ? t('venues.markUnvisited') : t('venues.markVisited')}
          aria-pressed={!!visited}
        >
          <Icon as={CheckCircle2} size={14} />
        </button>
      )}

      {/* Quick Google Maps link — opens the place in a new tab while reviewing */}
      {mapsUrl && (
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          onPointerDown={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
          className="self-stretch px-2.5 text-slate-300 hover:text-teal-500 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors shrink-0 border-l border-slate-100 dark:border-slate-700 flex items-center cursor-pointer"
          aria-label={t('itinerary.openInMaps')}
          title={t('itinerary.openInMaps')}
        >
          <Icon as={MapIcon} size={14} />
        </a>
      )}

      {/* Booking / reference link the activity carries — a URL saved from an
          imported document was only reachable by opening the edit form */}
      {!activity.isOrigin && activity.bookingUrl && (
        <a
          href={activity.bookingUrl}
          target="_blank"
          rel="noopener noreferrer"
          onPointerDown={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
          className="self-stretch px-2.5 text-slate-300 hover:text-teal-500 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors shrink-0 border-l border-slate-100 dark:border-slate-700 flex items-center cursor-pointer"
          aria-label={t('itinerary.openBooking')}
          title={t('itinerary.openBooking')}
        >
          <Icon as={ExternalLink} size={14} />
        </a>
      )}

      {/* Right action: checkbox (select mode) or trash */}
      {selectable ? (
        <button
          type="button"
          onPointerDown={e => e.stopPropagation()}
          onClick={onToggleSelect}
          className="self-stretch px-2.5 shrink-0 flex items-center border-l border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer"
          aria-label={t('common.select')}
        >
          <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
            isSelected
              ? 'border-teal-500 bg-teal-500'
              : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800'
          }`}>
            {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-white block" />}
          </span>
        </button>
      ) : deletable ? (
        <button
          type="button"
          onPointerDown={e => e.stopPropagation()}
          onClick={e => { e.stopPropagation(); setConfirmDelete(true); }}
          className="self-stretch px-2.5 text-slate-300 hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors shrink-0 border-l border-slate-100 dark:border-slate-700 flex items-center cursor-pointer"
          aria-label={t('common.delete')}
        >
          <Icon as={Trash2} size={14} />
        </button>
      ) : activity.isOrigin && onToggleStartFromPrev ? (
        <button
          type="button"
          disabled={startFromPrevPending}
          onPointerDown={e => e.stopPropagation()}
          onClick={e => { e.stopPropagation(); onToggleStartFromPrev(false); }}
          className="self-stretch px-2.5 text-teal-400 hover:text-teal-600 hover:bg-teal-100/50 dark:hover:bg-teal-900/30 transition-colors shrink-0 border-l border-teal-100 dark:border-teal-800 flex items-center cursor-pointer disabled:opacity-60 disabled:cursor-wait"
          aria-label={t('itinerary.startFromPrev.disable')}
          title={t('itinerary.startFromPrev.disable')}
        >
          {startFromPrevPending ? <Spinner size="xs" branded /> : <Icon as={X} size={14} />}
        </button>
      ) : null}

      <ConfirmModal
        open={confirmDelete}
        title={t('itinerary.delete.title')}
        body={isLocked ? t('itinerary.delete.bodyLocked') : t('itinerary.delete.body')}
        onConfirm={() => { onDelete(activity); setConfirmDelete(false); }}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
